// @ts-check
import { z } from "zod";

const formErrorMessages = document.querySelector("#form-error-messages");
if (!formErrorMessages) {
	throw new Error("No error messages found");
}
const errorMessages = JSON.parse(formErrorMessages.textContent);

const AddressSchema = z.object({
	street: z.string().min(2, errorMessages["street"]),
	postalCode: z.string().min(2, errorMessages["postalCode"]),
	city: z.string().min(2, errorMessages["city"]),
	country: z.string().min(2, errorMessages["country"]),
});

const PersonalDataSchema = z.object({
	title: z.string().min(2, errorMessages["requiredGeneric"]),
	firstName: z.string().min(2, errorMessages["firstName"]),
	lastName: z.string().min(2, errorMessages["lastName"]),
	email: z.email(errorMessages["email"]),
	phone: z.string().optional(),
});

const PrivacySchema = z.object({
	dataProcessing: z.boolean().refine((val) => val, {
		message: errorMessages["terms"],
	}),
	consentToContact: z.boolean().optional(),
	consentToContactPartner: z.boolean().optional(),
});

/**
 * Valid contact types for information request form.
 * Note: "message" is excluded as it's only used by dealer-request-form.
 */
export const InformationRequestContactTypeSchema = z.enum([
	"on-site-appointment",
	"showroom-appointment",
	"materials",
]);

const InformationRequestFormSchema = z.object({
	contactType: InformationRequestContactTypeSchema,
	preferredDay: z.array(z.string()).min(1, errorMessages["day"]),
	preferredTime: z.array(z.string()).min(1, errorMessages["time"]),
	materials: z.optional(z.array(z.string())),
	...PersonalDataSchema.shape,
	...AddressSchema.shape,
	...PrivacySchema.shape,
});

const InformationRequestFormNoDayTimeSchema = InformationRequestFormSchema.omit(
	{
		preferredDay: true,
		preferredTime: true,
	},
).extend({
	materials: z.array(z.string()).min(1, errorMessages["requiredGeneric"]),
});

const F04OrderFormSchema = z.object({
	...PersonalDataSchema.shape,
	...AddressSchema.shape,
	...PrivacySchema.shape,
});

/**
 * Validates data against a Zod schema and returns a standardized result.
 * @param {object} data - The data to validate.
 * @param {import('zod').ZodType} schema - The Zod schema to validate against.
 * @returns {import("./shared-schemas.js").ValidationResult} Object with success boolean and optional errors.
 */
const validateSchema = (data, schema) => {
	const result = schema.safeParse(data);

	if (!result.success) {
		return {
			success: false,
			errors: z.flattenError(result.error),
		};
	}

	return {
		success: true,
	};
};

/**
 * Enhances the given Zod schema to require province validation if the input data contains a "province" property.
 * @param {object} data - The input data object to check for the "province" property.
 * @param {import('zod').ZodObject} baseSchema - The base Zod schema to potentially enhance.
 * @returns {import('zod').ZodObject} The updated Zod schema, requiring "province" if present in data.
 */
const enableProvinceValidationIfRequired = (data, baseSchema) => {
	if (Object.hasOwn(data, "province")) {
		const ProvinceRequiredAddressSchema = z.object({
			...AddressSchema.shape,
			province: z.string().min(1, errorMessages["province"]),
		});
		baseSchema = z.object({
			...baseSchema.shape,
			...ProvinceRequiredAddressSchema.shape,
		});
	}

	return baseSchema;
};

/**
 * Enhances the given Zod schema to require role validation if the input data contains a "role" property.
 * @param {object} data - The input data object to check for the "role" property.
 * @param {import('zod').ZodObject} baseSchema - The base Zod schema to potentially enhance.
 * @returns {import('zod').ZodObject} The updated Zod schema, requiring "role" if present in data.
 */
const enableRoleValidationIfRequired = (data, baseSchema) => {
	if (Object.hasOwn(data, "role")) {
		const RoleRequiredPersonalDataSchema = z.object({
			...PersonalDataSchema.shape,
			role: z.string().min(2, errorMessages["requiredGeneric"]),
		});
		baseSchema = z.object({
			...baseSchema.shape,
			...RoleRequiredPersonalDataSchema.shape,
		});
	}

	return baseSchema;
};

/**
 * Extends the given Zod schema with a required "magazineLanguage" field.
 * @param {import('zod').ZodObject} baseSchema - The base Zod schema to enhance.
 * @returns {import('zod').ZodObject} The schema requiring "magazineLanguage".
 */
const enableMagazineLanguageValidation = (baseSchema) => {
	const MagazineLanguageRequiredPersonalDataSchema = z.object({
		...baseSchema.shape,
		magazineLanguage: z.string().min(2, errorMessages["magazineLanguage"]),
	});

	return MagazineLanguageRequiredPersonalDataSchema;
};

/**
 * Validates the F04 order form data.
 * @param {object} data - The form data to validate.
 * @returns {import("./shared-schemas.js").ValidationResult} Object with success boolean and optional errors.
 */
export const validateF04OrderForm = (data) => {
	let schema = enableProvinceValidationIfRequired(data, F04OrderFormSchema);
	schema = enableMagazineLanguageValidation(schema);
	schema = enableRoleValidationIfRequired(data, schema);
	return validateSchema(data, schema);
};

/**
 * Validates the information request form data using the appropriate schema based on the form type.
 * @param {object} data - The form data to validate.
 * @param {string} formType - The type of form to validate ("default" or "noDayTime" - default: "default").
 * @returns {object} An object containing the validation result:
 *   - {boolean} success - Indicates if validation passed.
 *   - {object} [errors] - Validation errors if validation failed.
 */
export const validateInformationRequestForm = (data, formType = "default") => {
	/** @type {z.ZodObject<z.ZodRawShape>} */
	let schema =
		formType === "default"
			? InformationRequestFormSchema
			: InformationRequestFormNoDayTimeSchema;

	schema = enableProvinceValidationIfRequired(data, schema);

	return validateSchema(data, schema);
};
